import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { useBottomSheetScrollableCreator } from '@gorhom/bottom-sheet';
import { useTranslation } from 'react-i18next';

import { Input, InputField, InputIcon, InputSlot } from '@/components/gluestack/input';
import { CloseIcon } from '@/components/gluestack/icon';
import { Text } from '@/components/gluestack/text';
import { VStack } from '@/components/gluestack/vstack';
import { DRAWER_SCROLL_CLASSES } from '@/components/ui/ContextSheet';
import { MessageView } from '@/components/ui/MessageView';
import { KlipyGifGridItem } from '@/components/ui/media/providers/klipy/KlipyGifGridItem';
import {
  dedupeKlipyGifs,
  mergeKlipyGifs,
} from '@/components/ui/media/providers/klipy/klipyGifUtils';
import { useAuth } from '@/components/providers/AuthProvider';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useDismissKeyboardOnScroll } from '@/hooks/useDismissKeyboardOnScroll';
import { useKlipyApiKey } from '@/hooks/useKlipyApiKey';
import type { IMediaItem, IMediaPickerProps } from '@/models/Media';
import { KlipyApi } from '@/services/media/providers/klipy/api';
import type { IKlipyGif } from '@/services/media/providers/klipy/types';

const PAGE_SIZE = 24;
const GRID_GAP = 8;
const GRID_COLUMNS = 2;
const isWeb = Platform.OS === 'web';

export function KlipyGifPicker({ type, displayName, onSelect, onClose }: IMediaPickerProps) {
  const { t, i18n } = useTranslation();
  const apiKey = useKlipyApiKey();
  const { accountStatus } = useAuth();
  const isLargeScreen = useIsLargeScreen();
  const gorhomFlashListScroll = useBottomSheetScrollableCreator();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [gifs, setGifs] = useState<IKlipyGif[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const dismissKeyboardOnScroll = useDismissKeyboardOnScroll({
    mode: 'anyDirection',
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadPage = useCallback(
    async (nextPage: number, reset: boolean) => {
      if (!apiKey) return;

      if (reset) {
        setIsLoading(true);
        setError(null);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const locale = i18n.language === 'default' ? 'en' : i18n.language.split('-')[0];
        const customerId = accountStatus?.MemberId ? String(accountStatus.MemberId) : undefined;
        const results = debouncedQuery
          ? await KlipyApi.gifs.search(apiKey, {
              query: debouncedQuery,
              page: nextPage,
              perPage: PAGE_SIZE,
              customerId,
              locale,
            })
          : await KlipyApi.gifs.trending(apiKey, {
              page: nextPage,
              perPage: PAGE_SIZE,
              customerId,
              locale,
            });

        setGifs((current) =>
          reset ? dedupeKlipyGifs(results) : mergeKlipyGifs(current, results)
        );
        setPage(nextPage);
        setHasMore(results.length >= PAGE_SIZE);
        setError(null);
      } catch (err) {
        if (reset) {
          setGifs([]);
          setError(err);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [accountStatus?.MemberId, apiKey, debouncedQuery, i18n.language]
  );

  useEffect(() => {
    void loadPage(1, true);
  }, [loadPage]);

  const handleSelect = useCallback(
    (gif: IKlipyGif) => {
      const item: IMediaItem = {
        Provider: 'klipy',
        Type: type,
        Id: gif.slug,
      };
      const shareCtx = debouncedQuery ? { query: debouncedQuery } : undefined;
      onSelect(item, shareCtx);
      onClose();
    },
    [debouncedQuery, onClose, onSelect, type]
  );

  const handleEndReached = useCallback(() => {
    if (!isLoading && !isLoadingMore && hasMore) {
      void loadPage(page + 1, false);
    }
  }, [hasMore, isLoading, isLoadingMore, loadPage, page]);

  const handleSubmitSearch = useCallback(() => {
    setDebouncedQuery(searchQuery.trim());
  }, [searchQuery]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const renderItem = useCallback(
    ({ item }: { item: IKlipyGif }) => (
      <View style={{ padding: GRID_GAP / 2 }}>
        <KlipyGifGridItem gif={item} onPress={handleSelect} />
      </View>
    ),
    [handleSelect]
  );

  const keyExtractor = useCallback((item: IKlipyGif) => item.slug, []);

  const listFooter = useMemo(
    () => (
      <>
        {isLoadingMore ? (
          <View className="py-4">
            <ActivityIndicator />
          </View>
        ) : null}
        <View className="items-center py-3">
          <Text size="xs" className="text-typography-500">
            {t('media.powered_by', { provider: displayName })}
          </Text>
        </View>
      </>
    ),
    [displayName, isLoadingMore, t]
  );

  const useBottomSheetScroll = !isLargeScreen && !isWeb;

  const gifPickerScrollClasses = useMemo(
    () => ({
      className: DRAWER_SCROLL_CLASSES.className,
      contentContainerClassName: isWeb && isLargeScreen ? 'mr-2' : 'mr-5',
    }),
    [isLargeScreen]
  );

  if (!apiKey) {
    return (
      <View className="flex-1 items-center justify-center px-6">
        <MessageView type="warning" text={t('media.failed_to_load')} disableHeight />
      </View>
    );
  }

  return (
    <VStack className="min-h-0 flex-1">
      <View className="shrink-0 pb-3">
        <Input variant="rounded">
          <InputField
            placeholder={t('media.search_placeholder', { provider: displayName })}
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmitEditing={handleSubmitSearch}
            returnKeyType="search"
            autoCorrect={false}
            autoCapitalize="none"
          />
          {searchQuery.length > 0 && (
            <InputSlot className="pr-2" onPress={clearSearch}>
              <InputIcon as={CloseIcon} />
            </InputSlot>
          )}
        </Input>
      </View>

      {isLoading ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator />
        </View>
      ) : error ? (
        <View className="flex-1 items-center justify-center px-6">
          <MessageView type="error" text={t('media.failed_to_load')} disableHeight />
        </View>
      ) : gifs.length === 0 ? (
        <View className="flex-1 items-center justify-center px-6">
          <MessageView type="info" text={t('media.no_results')} disableHeight />
        </View>
      ) : (
        <View style={{ flex: 1, flexBasis: 0, minHeight: 0 }}>
          <FlashList
            style={{ flex: 1 }}
            {...gifPickerScrollClasses}
            showsVerticalScrollIndicator
            data={gifs}
            masonry
            numColumns={GRID_COLUMNS}
            optimizeItemArrangement
            keyExtractor={keyExtractor}
            renderItem={renderItem}
            keyboardShouldPersistTaps="handled"
            onEndReached={handleEndReached}
            onEndReachedThreshold={0.5}
            ListFooterComponent={listFooter}
            {...(useBottomSheetScroll
              ? { renderScrollComponent: gorhomFlashListScroll, bounces: false }
              : {})}
            {...dismissKeyboardOnScroll}
          />
        </View>
      )}
    </VStack>
  );
}
